import { HB_IMG_RETRY } from "@/constants/cacheKey";
import { EVENT } from "@/constants/event";
import { PIN_CARD_MAX_HEIGHT } from "@/constants/pin";
import useAppStore from "@/context/store";
import { gdSlsLog } from "@/utils/gdSlsRum";
import imageUrl from "@/utils/imageUrl";
import { imageUrlConfig } from "@/utils/imageUrlConfig";
import classNames from "classnames";
import { useEffect } from "react";
import styles from "./index.module.less";

// 取一张默认头像图片数据重试
export const exampleFile = {
  bucket: "hbimg",
  height: 300,
  key: "654953460733026a7ef6e101404055627ad51784a95c-B6OFs4",
  type: "image/jpeg",
  width: 300,
};
const loadImage = (domain: string, bucketHandMethod: number) => {
  const bucket = imageUrlConfig.getBucket(exampleFile.bucket, bucketHandMethod);
  const url = imageUrl(exampleFile, { domain, bucket, width: 75, rect: "sq" });

  return new Promise<{ domain: string; bucketHandMethod: number }>(
    (resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        // 加载成功，返回配置
        resolve({ domain, bucketHandMethod });
      };
      image.onerror = () => {
        // 加载失败
        reject(new Error(`Failed to load image from ${domain}`));
      };
      // 添加随机参数避免缓存
      image.src = `${url}?time=${Date.now()}`;
    }
  );
};

// 带超时的 Promise.race，任意一个成功即返回
const raceWithTimeout = <T,>(
  promises: Promise<T>[],
  timeout: number
): Promise<T> => {
  return new Promise((resolve, reject) => {
    let settled = false;
    let failCount = 0;
    const total = promises.length;

    // 超时处理
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        reject(new Error("Timeout"));
      }
    }, timeout);

    promises.forEach((p) => {
      p.then((result) => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          resolve(result);
        }
      }).catch(() => {
        failCount++;
        // 所有请求都失败了
        if (failCount === total && !settled) {
          settled = true;
          clearTimeout(timer);
          reject(new Error("All requests failed"));
        }
      });
    });
  });
};

// 按优先级加载图片配置：优先 bucketHandMethod === 3，超时或失败后再尝试其他配置
const loadImageWithPriority = async (
  imageConfigList: Array<{ domain?: string; bucketHandMethod?: number }>
): Promise<{ domain: string; bucketHandMethod: number } | void> => {
  const PRIORITY_TIMEOUT = 3000; // 优先配置超时时间 3s

  // 过滤有效配置（domain 和 bucketHandMethod 都存在）
  const validConfigs = imageConfigList.filter(
    (item): item is { domain: string; bucketHandMethod: number } =>
      item.domain !== undefined && item.bucketHandMethod !== undefined
  );

  // 分组：优先配置 vs 普通配置
  const priorityConfigs = validConfigs.filter(
    (item) => item.bucketHandMethod === 3
  );
  const normalConfigs = validConfigs.filter(
    (item) => item.bucketHandMethod !== 3
  );

  // 先尝试优先配置
  if (priorityConfigs.length > 0) {
    try {
      const result = await raceWithTimeout(
        priorityConfigs.map((item) =>
          loadImage(item.domain, item.bucketHandMethod)
        ),
        PRIORITY_TIMEOUT
      );
      return result;
    } catch {
      // 优先配置全部失败或超时，继续尝试普通配置
    }
  }

  // 尝试普通配置
  if (normalConfigs.length > 0) {
    return raceWithTimeout(
      normalConfigs.map((item) =>
        loadImage(item.domain, item.bucketHandMethod)
      ),
      PRIORITY_TIMEOUT
    ).catch(() => {
      // do nothing
      console.warn("配置全部失败或超时");
    });
  }

  throw new Error("No available image config");
};
// 初始化
if (typeof window !== "undefined") {
  const errorList = [];
  let timeoutLable = null;

  const switchImageUrlConfig = (e) => {
    errorList.push(e.detail.src || "Empty");

    // 20s内清除错误列表，防止长期访问累计后切换域名
    clearTimeout(timeoutLable);
    timeoutLable = setTimeout(() => {
      errorList.length = 0;
    }, 20 * 1000);

    // 错误上限，自动切换域名
    if (errorList.length > 10) {
      let isRetry = sessionStorage.getItem(HB_IMG_RETRY);
      // 判断是否重试过
      if (isRetry !== "true") {
        const imageConfigList =
          useAppStore.getState().config?.imageConfigList || [];
        // 根据杂货铺配置进行重试，找到最快能正常请求的图片配置
        // 优先加载 bucketHandMethod === 3 的配置，超时或失败后再加载其他配置
        loadImageWithPriority(imageConfigList).then((res) => {
          sessionStorage.setItem(HB_IMG_RETRY, "true");

          if (!res) {
            return;
          }

          imageUrlConfig.set(res);

          gdSlsLog({
            type: "autoSwitchImage",
            message: "图片自动切换域名：切换成功",
            detail: res,
          });

          setTimeout(() => {
            // 延迟刷新，预留日志上报时间
            window.location.reload();
          }, 200);
        });
      }
      // 解绑事件
      window.removeEventListener(EVENT.IMAGE_LOAD_ERROR, switchImageUrlConfig);
    }
  };

  window.addEventListener(EVENT.IMAGE_LOAD_ERROR, switchImageUrlConfig);
}

export default function LoadErrorImage({
  src,
  srcset = "",
  width,
  height,
  limit = false,
}) {
  useEffect(() => {
    if (typeof window !== "undefined") {
      const event = new CustomEvent(EVENT.IMAGE_LOAD_ERROR, {
        detail: { src },
      });

      window.dispatchEvent(event);
    }
  }, [src]);

  return (
    <div
      className={classNames(
        styles.error,
        height >= PIN_CARD_MAX_HEIGHT && limit ? styles.limit : null,
        "hb-image-error"
      )}
      style={{ width: `${width}px`, height: `${height}px` }}
      data-img-src={src}
      data-img-srcset={srcset}
    />
  );
}
